import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmailRequestType,
  LabelId,
  UserCommunication,
} from '@this+that/models';
import log from 'loglevel';

import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import {
  useInboxEntriesStore,
  useUserCommunicationsStore,
} from '../../../store';
import { getLabelIdsForAction } from '../../../utils/emailLabelHelpers';
import { useCustomToast } from '../../useCustomToast';
import {
  applyOptimisticEntryLabelHide,
  revertOptimisticEntryLabelHide,
} from '../shared';

export const useTrashUserCommunicationForDoBox = () => {
  const { t } = useTranslation();

  const { errorMessage, warningMessage } = useCustomToast();

  const handleTrashUserCommunication = useCallback(
    async (
      messageIds: string | string[],
      options?: {
        onSuccess?: () => void;
        currentTab?: string | null;
      },
    ) => {
      const {
        setDeleteLoading,
        updateUserCommunicationLabels,
        getUserCommunicationByMessageId,
      } = useUserCommunicationsStore.getState();
      const {
        removeUserCommunicationsFromInboxEntries,
        removeInboxEntry,
        getCommunicationsByInboxEntryId,
        updateUserCommunicationLabelsInInboxEntries,
      } = useInboxEntriesStore.getState().taskManager.activeTaskGroup;
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) {
        return;
      }

      const communicationsToTrash = messageIdsArray
        .map((messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);
          if (!userCommunication) {
            log.error(
              `UserCommunication with messageId ${messageId} not found`,
            );
            return null;
          }
          return { messageId, communication: userCommunication };
        })
        .filter(
          (
            item,
          ): item is { messageId: string; communication: UserCommunication } =>
            item !== null,
        )
        .filter(
          ({ communication }) =>
            !communication.labelIds?.includes(LabelId.TRASH),
        );

      if (communicationsToTrash.length === 0) {
        warningMessage(t('inbox.userCommunication.actions.alreadyInTrash'));
        return;
      }

      const linkedEntryIds = Array.from(
        new Set(
          communicationsToTrash.flatMap(
            ({ communication }) => communication.inboxEntryIds ?? [],
          ),
        ),
      );

      const entryIdsToRemove = linkedEntryIds.filter((entryId) => {
        const currentCommunications = getCommunicationsByInboxEntryId(entryId);
        const remainingCommunications = (currentCommunications || []).filter(
          (comm) =>
            !communicationsToTrash.some((c) => c.messageId === comm.messageId),
        );
        return remainingCommunications.length === 0;
      });

      const labelBackupMap = new Map<string, LabelId[]>();
      communicationsToTrash.forEach(({ messageId, communication }) => {
        labelBackupMap.set(messageId, communication.labelIds || []);
        const newLabelIds = getLabelIdsForAction(
          communication.labelIds,
          'trash',
        );
        updateUserCommunicationLabels(messageId, newLabelIds);
      });

      applyOptimisticEntryLabelHide(
        updateUserCommunicationLabelsInInboxEntries,
        communicationsToTrash.map(({ messageId }) => messageId),
        'trash',
      );

      removeUserCommunicationsFromInboxEntries(messageIdsArray);

      options?.onSuccess?.();

      setDeleteLoading(true);

      try {
        const operations = communicationsToTrash.map(
          async ({ messageId, communication }) => {
            try {
              if (!communication.userEmailAddress) {
                throw new Error('No user email address found in communication');
              }

              if (!communication.platform) {
                throw new Error('No platform found in communication');
              }

              await modifyEmail({
                userEmailAddress: communication.userEmailAddress,
                platform: communication.platform,
                messageId: communication.messageId,
                action: EmailRequestType.TRASH,
              });

              return { messageId, success: true, communication };
            } catch (error) {
              log.error(
                `Failed to trash email for messageId ${messageId}:`,
                error,
              );
              return { messageId, success: false, error, communication };
            }
          },
        );

        const results = await Promise.all(operations);
        const failed = results.filter((result) => !result.success);

        if (failed.length === 0) {
          log.info(
            `${communicationsToTrash.length} messages moved to trash successfully`,
          );
          if (entryIdsToRemove.length > 0) {
            await Promise.all(
              entryIdsToRemove.map((entryId) => removeInboxEntry(entryId)),
            );
          }
        }

        if (failed.length > 0) {
          const failedMessageIds = failed.map((result) => result.messageId);
          revertOptimisticEntryLabelHide(
            updateUserCommunicationLabelsInInboxEntries,
            failedMessageIds,
            labelBackupMap,
          );

          failed.forEach(({ messageId }) => {
            const previousLabels = labelBackupMap.get(messageId);
            if (previousLabels) {
              updateUserCommunicationLabels(messageId, previousLabels);
            }
          });

          log.warn(
            `${failed.length} operations failed out of ${communicationsToTrash.length}`,
          );

          errorMessage(
            failed.length === communicationsToTrash.length
              ? t('inbox.userCommunication.actions.trashError')
              : t('inbox.userCommunication.actions.partialTrashError', {
                  failed: failed.length,
                  total: communicationsToTrash.length,
                }),
          );
        }
      } catch (error) {
        const trashedMessageIds = communicationsToTrash.map(
          ({ messageId }) => messageId,
        );
        revertOptimisticEntryLabelHide(
          updateUserCommunicationLabelsInInboxEntries,
          trashedMessageIds,
          labelBackupMap,
        );

        communicationsToTrash.forEach(({ messageId }) => {
          const previousLabels = labelBackupMap.get(messageId);
          if (previousLabels) {
            updateUserCommunicationLabels(messageId, previousLabels);
          }
        });

        log.error(error);
        errorMessage(t('inbox.userCommunication.actions.trashError'));
      } finally {
        setDeleteLoading(false);
      }
    },
    [errorMessage, warningMessage, t],
  );

  const extractMessageIdFromSelectedItems = useCallback(
    (selectedItems: Set<string>) => {
      return Array.from(selectedItems).map((messageId) => messageId);
    },
    [],
  );

  return {
    handleTrashUserCommunication,
    extractMessageIdFromSelectedItems,
  };
};
